import { debounceTime } from 'rxjs/operators';
import { QueryParameter } from './../../../interfaces/queryString';
import { SapiQueryResponse, SapiService } from './../../../services/sapi.service';
import { TranslateService } from '@ngx-translate/core';
import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Datafeed } from '../../../interfaces/datafeed';
import { Subscription } from 'rxjs';
import { ClipboardService } from 'ngx-clipboard';
import { DialogDefinition, ModalDefinition, OverlayService } from 'app/services/overlay.service';
import { MediaProfile } from 'app/interfaces/mediaProfile';

@Component({
	selector: 'datafeed-details-metadata',
	templateUrl: './datafeed-details-metadata.component.html',
	styleUrls: ['./datafeed-details-metadata.component.scss']
})

export class DatafeedDetailsMetadataComponent implements OnInit, OnDestroy {
	@ViewChild('createPresetTemplate') createPresetTemplate: TemplateRef<any>;
	@ViewChild('matSelect') matSelect = null;
	@Input() datafeed: Datafeed;
	@Input() form: NgForm;
	@Input() isNew: boolean;

	subscriptions = new Subscription();

	presetModal: ModalDefinition;
	mediaProfiles: any[] = [];

	feedTypeOptions = [
		{
			label: this.translate.instant('DEFAULT'),
			value: 'default'
		},
		{
			label: this.translate.instant('RENDERED_VIDEO'),
			value: 'renderedVideo'
		},
		{
			label: this.translate.instant('RSS_FEED_IMPORT'),
			value: 'rssFeedImport'
		},
		{
			label: this.translate.instant('XML_UPLOAD_IMPORT'),
			value: 'xmlUploadImport'
		},
		// TODO: selecting mediaclip here will allow the mapping of fields to mediaclip (custom) metadata properties   (will be added later)
		// {
		// 	label: this.translate.instant('MEDIACLIP'),
		// 	value: 'mediaclip'
		// }
	];

	constructor(
		private translate: TranslateService,
		private sapiService: SapiService,
		private clipboardService: ClipboardService,
		private overlayService: OverlayService
	) { }

	ngOnInit() {
		if (this.isNew) {
			this.datafeed.transferprotocol = 's3';
			this.datafeed.transcodingProfile = 'default';
		}

		this.getProfiles();
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	onFeedTypeChanged() {
		if (this.datafeed?.name) {
			this.updateCode(this.datafeed.name);
		}
		if (this.datafeed?.feedType === 'renderedVideo') {
			this.datafeed.storageMode = 's3';
		} else {
			this.datafeed.storageMode = '';
		}
	}

	onNameInit() {
		setTimeout(() => {
			if (this.form.controls.name) {
				this.subscriptions.add(this.form.controls.name.valueChanges.pipe(debounceTime(250)).subscribe((v) => {
					this.onNameUpdated(v);
				}));
			}
		});
	}

	onNameUpdated(name: string) {
		if (name) {
			this.updateCode(name);
		}
	}

	copyToClipboard(str: string, msg: string): void {
		this.clipboardService.copyFromContent(str);
		this.overlayService.createToast(this.translate.instant(msg), 'copy');
	}

	updateCode(name: string) {
		if (this.datafeed && this.datafeed.feedType === 'xmlUploadImport' && !this.datafeed.id) {
			this.datafeed.code = name ? name.replace(/-/g, '*').replace(/[^\w\-]{1,}/g, '_').toLowerCase() : '';

			const queryParams: QueryParameter[] = [{ name: 'q', values: ['code:' + this.datafeed.code] }];

			this.sapiService.query('datafeed', queryParams).subscribe((sapiQueryResponse: SapiQueryResponse) => {
				if (sapiQueryResponse && sapiQueryResponse.totalAmount > 0) {
					this.form.form.controls.code.setErrors({ 'not-unique': true });
					this.form.form.controls.code.markAsTouched();
					this.form.form.controls.code.markAsDirty();
				}
			});
		}
	}

	onCallbackUrlChanged(url: string) {
		const urlRegex = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/g;

		if (url && !urlRegex.test(url)) {
			this.form.form.controls.callbackUrl.setErrors({
				'invalid-format': true
			});
		}
	}

	getProfiles(newProfile?) {
		this.mediaProfiles = [];
		this.sapiService.getUrl('/mediaprofile').subscribe((mediaProfile: MediaProfile) => {
			if (mediaProfile && mediaProfile.items) {
				mediaProfile.items.forEach((item) => {
					this.mediaProfiles.push(item);
					if (newProfile) {
						this.datafeed.transcodingProfile = newProfile;
					}
				});
				const profileSet = this.mediaProfiles.find(setProfile => setProfile.name === this.datafeed.transcodingProfile);
				if (!profileSet) {
					this.datafeed.transcodingProfile = 'default';
				};
			}
		});
	}

	deleteProfile(profile) {
		const config = { entity: this.translate.instant('PRESET').toLowerCase(), id: profile.name };;
		const dialog: DialogDefinition = {
			icon: 'trash',
			title: this.translate.instant('DELETE_ENTITY', config),
			contents: this.translate.instant('DELETE_ENTITY_CONFIRM', config),
			onConfirm: () => {
				this.overlayService.setLoading(true);
				this.sapiService.delete('mediaprofile', profile.id).subscribe(
					(data) => {
						const msg = this.translate.instant('ENTITY_DELETED', config);
						this.overlayService.createToast(msg, 'info-circle');
						this.getProfiles();
						this.overlayService.setLoading(false);
					},
					(err) => {
						this.overlayService.createToast(this.translate.instant('ERROR'), 'warning');
						this.overlayService.setLoading(false);
					}
				);
				this.overlayService.setLoading(false);
			}
		};
		this.overlayService.createDialog(dialog);
		this.form.form.markAsDirty();
	}

	createPreset() {
		this.matSelect.close();
		this.presetModal = {
			icon: '',
			title: 'Create new preset',
			component: {
				name: 'custom-content',
				templateRef: this.createPresetTemplate
			}
		};
		this.overlayService.createModal(this.presetModal);
	}

	closeModal(newProfile) {
		this.overlayService.removeModal(this.presetModal);
		this.getProfiles(newProfile);
		this.form.form.markAsDirty();
	}
}
